import { Injectable, OnDestroy } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType} from '@azure/msal-browser';
import { BehaviorSubject, filter } from 'rxjs';
import { LoaderService } from '../../../shared/components/dialog-spinner/loader.service';
import { PortalAccount } from '../../auth/PortalAccount';
import { AccountService } from '../../../shared/account/account.service';
import { environment } from '../../../../environments/environment';

// TODO: confirm the real Azure AD claim key with the identity/backend team and swap this placeholder.
const DEPARTMENT_CLAIM_KEY = 'extension_Department';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {

  portalAccount = new BehaviorSubject<PortalAccount | undefined>(undefined);

  constructor(
    private readonly msal: MsalService,
    private readonly dialogSpinner: LoaderService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly accountService: AccountService
  ) { }

  ngOnDestroy(): void {
    this.portalAccount.next(undefined);
    this.portalAccount.complete();
  }

  public get portalAccount$(): BehaviorSubject<PortalAccount | undefined> {
    return this.portalAccount;
  }

  init() {
    this.msal.handleRedirectObservable().subscribe();

    const accounts = this.msal.instance.getAllAccounts();

    if (accounts.length == 0) {
      this.dialogSpinner.show();
    } else {
      const account = this.msal.instance.getActiveAccount() ?? accounts[0];
      this.msal.instance.setActiveAccount(account);

      this.msal.instance.acquireTokenSilent({
        scopes: environment.msalConfig.guard.authRequest.scopes,
        account
      }).then(result => this.handleLoginSuccess(result.account, result.accessToken))
        .catch(e => {
          this.dialogSpinner.hide();
          console.error('Silent token acquisition failed during init():', e);
        });
    }

   this.subscribeToEvents();
  }

  private async subscribeToEvents() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
      )
      .subscribe((result) => {
        const payload = result.payload as AuthenticationResult;
        this.handleLoginSuccess(payload.account, payload.accessToken)
          .catch(e => console.error('handleLoginSuccess failed after login/token event:', e));
      });
  }

  private async handleLoginSuccess(msalAccount: any, token?: string) {
    try {
      if (!msalAccount || !msalAccount.idTokenClaims || !token) {
        this.dialogSpinner.hide();
        return;
      }

      this.msal.instance.setActiveAccount(msalAccount);

      // next, get account information from the portal      
      let account = await this.accountService.getAccountDetailByUsername(msalAccount.idTokenClaims["preferred_username"]);
      account.msalAccount = msalAccount;
      account.token = token;
      // account.department = msalAccount.idTokenClaims[DEPARTMENT_CLAIM_KEY];
      account.department = 'super-admin';

      this.portalAccount.next(account);
      this.dialogSpinner.hide();
      return;
    } catch (e) {
      throw new Error(`Error while handling login: ${JSON.stringify(e)}`);
    }
  }

  logout() {
    if (this.portalAccount) {
      this.msal.logout({ account: this.portalAccount.value?.msalAccount, postLogoutRedirectUri: window.location.origin });
    }
  }

  isUserOrgAdmin() {
    return this.userHasRole(2);
  }

  isUserSysAdmin() {
    return this.userHasRole(1);
  }

  userHasRole(roleId: number) {
    return this.portalAccount?.value?.roles?.some(role => role.role_id === roleId) || false;
  }
}